import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { FinancialDocument } from 'src/features/financialDocuments/utils/types';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { FINANCIAL_DOCUMENT_RESOURCE } from 'src/features/financialDocuments/api/useGetFinancialDocument';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRegenerateFinancialDocument } from 'src/features/financialDocuments/api/useRegenerateFinancialDocument';

interface RegenerateFinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedFinancialDocument: FinancialDocument;
    children?: React.ReactNode;
}

function RegenerateFinancialDocumentFormModal<TData>({
    selectedFinancialDocument,
    children,
}: RegenerateFinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: regenerateFinancialDocument } = useRegenerateFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.regenerateFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.regenerateFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...financialDocumentsMessages.regenerateFinancialDocument },
                subTitle: {
                    ...financialDocumentsMessages.regenerateFinancialDocumentSubtitle,
                },
                icon: ICON_MAP.INVOICE,
                confirmationMessage: { ...financialDocumentsMessages.confirmRegenerateFinancialDocument },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: financialDocumentsMessages.regenerate,
                callbackFn: () => {
                    return (
                        regenerateFinancialDocument({
                            documentId: selectedFinancialDocument.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default RegenerateFinancialDocumentFormModal;
